/*
    Компонент для вывода информации по ФИО
    Обогащает и форматирует полученные в props данные с помощью domain/name и SuggestionsService,
    по готовности данных выполняет readyCallback, чтобы сообщить родителю о готовности

    Меняет отображение и часть логики в зависимости от того, где выводится
    (suggestion или clean, granular)

    Заново запускает обогащение данных при их изменении.
    Раскрывает для родительского компонента свои данные validationMessage, isGood и fio
    (используются в демо-форме гранулярных подсказок по адресам для вывода сообщения и пола)
*/

import helper from "../../../domain/name";
import SuggestionsService from "../../service";

const service = new SuggestionsService("NAME");

const template = /* html */`
<div class="flex-column gap-2">
    <div
        :class="{'has-success': isGood, 'has-error': !isGood}"
        class="feedback_wrapper"
        v-if="validationMessage && type === 'suggestion' && !granular"
    >
        <span
            data-ref="feedback"
            class="feedback"
            >
                <i
                    class="fa fa-lg"
                    :class="[isGood?'fa-check-circle':'fa-exclamation-circle']"
            ></i> [[ validationMessage ]]
        </span>
    </div>
    <div class="flex-column items-start flex-row-sm gap-2 gap-sm-4 max-w-12">
        <div class="grid results-block results-block--left items-start gap-sm-1" v-if="!granular">
            <span
                class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
            >
                [[ type === "suggestion" ? 'ФИО по полям' : 'Результат разбора' ]]
            </span>
            <span class="color-gray">Фамилия</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="surname">
                [[ fio.surname || '—' ]]
            </span>
            <span class="color-gray">Имя</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="name">
                [[ fio.name || '—' ]]
            </span>
            <span class="color-gray">Отчество</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="patronymic">
                [[ fio.patronymic || '—' ]]
            </span>
            <span class="color-gray">Пол</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="gender">
                [[ genderWithSign ]]
            </span>
        </div>
        <div class="grid results-block items-start gap-sm-1" v-if="fio.result_genitive">
            <span
                class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
            >
                Падежи
            </span>
            <span class="color-gray">Именительный: кто?</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="declension-nominative">
                [[ fio.result ]]
            </span>
            <span class="color-gray">Родительный: кого?</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="declension-genitive">
                [[ fio.result_genitive ]]
            </span>
            <span class="color-gray">Дательный: кому?</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="declension-dative">
                [[ fio.result_dative ]]
            </span>
            <span class="color-gray">Творительный: кем?</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="declension-ablative">
                [[ fio.result_ablative ]]
            </span>
            <span class="grid-col-span-sm-2" v-if="type === 'suggestion'">
                Склонение подключают отдельно,
                через <a href="/api/clean/name/">API стандартизации</a>
            </span>
        </div>
    </div>
</div>
`;

export default {
    name: "NameResults",
    template,
    data() {
        return { fio: {} };
    },
    props: {
        suggestion: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: "suggestion",
            validator(value) {
                return ["suggestion", "clean"].includes(value);
            },
        },
        granular: {
            type: Boolean,
            default: false,
        },
        readyCallback: {
            type: Function,
            default: () => {},
        },
    },
    computed: {
        genderWithSign() {
            return helper.genderWithSign(this.fio);
        },

        isGood() {
            return helper.isGood(this.fio);
        },

        validationMessage() {
            return helper.validationMessage(this.fio);
        },
    },
    methods: {
        getComputedData() {
            return new Promise(resolve => {
                service.enrich(
                    this.suggestion.unrestricted_value || this.suggestion.query,
                    this.suggestion.query ? null : this.suggestion,
                )
                    .done(fio => {
                        this.fio = fio;
                        resolve();
                    })
                    .fail(fio => {
                        this.fio = fio;
                        resolve();
                    });
            });
        },
    },
    watch: {
        suggestion: {
            handler() {
                if (this.type === "clean") {
                    this.fio = this.suggestion;
                    this.readyCallback();
                    return;
                }
                this.getComputedData()
                    .then(() => {
                        this.readyCallback();
                    });
            },
            deep: true,
        },
    },
    mounted() {
        if (this.type === "clean") {
            this.fio = this.suggestion;
            this.readyCallback();
            return;
        }
        this.getComputedData()
            .then(() => {
                this.readyCallback();
            });
    },
    expose: ["isGood", "validationMessage", "fio"],
};
